import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import MobileFormDialog from "../../../components/ui/mobile-form-dialog";
import DocumentsManager from "../../../components/DocumentsManager";
import { Plus, Trash2, Clock } from "lucide-react";
import { API } from "../../../App";
import { useCamposDoDocumento, AVISO_DOCUMENTO } from "../../../lib/camposDoDocumento";
import {
  QUAL_LABELS, QUAL_NO_DATES, minutesToHHmmInput, hhmmInputToMinutes,
} from "./diUtils";

export default function InstructorForm({ open, instructor, onClose, onSaved }) {
  const isEdit = !!instructor?.id;
  const bloqueados = useCamposDoDocumento("instructor", instructor?.id);
  const defaultStatus = isEdit ? (instructor?.license_status ?? null) : "R";
  const [form, setForm] = useState(() => ({
    name: instructor?.name || "",
    pu_number: instructor?.pu_number || "",
    pu_license_valid_until: instructor?.pu_license_valid_until || "",
    medical_cert_number: instructor?.medical_cert_number || "",
    medical_valid_until: instructor?.medical_valid_until || "",
    proof_check_date: instructor?.proof_check_date || "",
    instructor_license_issued_at: instructor?.instructor_license_issued_at || "",
    instructor_license_valid_until: instructor?.instructor_license_valid_until || "",
    license_status: defaultStatus,
    notes: instructor?.notes || "",
    conta_email: instructor?.conta_email || "",
    qualifications: instructor?.qualifications || [],
    initial_pic_hhmm: minutesToHHmmInput(instructor?.initial_pic_minutes || 0),
    initial_instructor_hhmm: minutesToHHmmInput(instructor?.initial_instructor_minutes || 0),
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      name: instructor?.name || "",
      pu_number: instructor?.pu_number || "",
      pu_license_valid_until: instructor?.pu_license_valid_until || "",
      medical_cert_number: instructor?.medical_cert_number || "",
      medical_valid_until: instructor?.medical_valid_until || "",
      proof_check_date: instructor?.proof_check_date || "",
      instructor_license_issued_at: instructor?.instructor_license_issued_at || "",
      instructor_license_valid_until: instructor?.instructor_license_valid_until || "",
      license_status: isEdit ? (instructor?.license_status ?? null) : "R",
      notes: instructor?.notes || "",
      conta_email: instructor?.conta_email || "",
      qualifications: instructor?.qualifications || [],
      initial_pic_hhmm: minutesToHHmmInput(instructor?.initial_pic_minutes || 0),
      initial_instructor_hhmm: minutesToHHmmInput(instructor?.initial_instructor_minutes || 0),
    });
  }, [instructor, isEdit]);

  const updateQual = (i, key, value) => {
    setForm((f) => {
      const next = [...f.qualifications];
      next[i] = { ...next[i], [key]: value };
      if (key === "type" && QUAL_NO_DATES.has(value)) {
        next[i].valid_from = "";
        next[i].valid_until = "";
      }
      return { ...f, qualifications: next };
    });
  };
  const addQual = () =>
    setForm((f) => ({
      ...f,
      qualifications: [...f.qualifications, { type: "instr_teorica", valid_from: "", valid_until: "" }],
    }));
  const removeQual = (i) =>
    setForm((f) => ({ ...f, qualifications: f.qualifications.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.name.trim() || !form.pu_number.trim()) {
      toast.error("Nome e PU são obrigatórios"); return;
    }
    if (form.instructor_license_valid_until && !form.instructor_license_issued_at) {
      toast.error("Indica a Data de Emissão da Licença de Instrutor"); return;
    }
    // A validade do certificado médico é obrigatória; o Cartão de Cidadão e a licença PU são carregados em Documentos anexos
    if (!form.medical_valid_until) { toast.error("Indica a validade do certificado médico"); return; }
    const initial_pic_minutes = hhmmInputToMinutes(form.initial_pic_hhmm);
    const initial_instructor_minutes = hhmmInputToMinutes(form.initial_instructor_hhmm);
    if (initial_instructor_minutes > initial_pic_minutes) {
      toast.error("Horas iniciais como Instrutor não podem exceder horas iniciais como PIC."); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        medical_cert_number: form.medical_cert_number || null,
        medical_valid_until: form.medical_valid_until || null,
        instructor_license_issued_at: form.instructor_license_issued_at || null,
        instructor_license_valid_until: form.instructor_license_valid_until || null,
        pu_license_valid_until: form.pu_license_valid_until || null,
        initial_pic_minutes,
        initial_instructor_minutes,
        qualifications: form.qualifications.map((q) => ({
          type: q.type,
          valid_from: QUAL_NO_DATES.has(q.type) ? null : (q.valid_from || null),
          valid_until: QUAL_NO_DATES.has(q.type) ? null : (q.valid_until || null),
          anac_ref: q.anac_ref || null,
          examiner_test: q.examiner_test || null,
        })),
      };
      // A conta de acesso só se envia quando muda (vazio desliga); sem isto, gravar a ficha podia desligar uma conta
      if (isEdit && (form.conta_email || "").trim() !== (instructor?.conta_email || "")) payload.conta_email = (form.conta_email || "").trim();
      else delete payload.conta_email;
      delete payload.initial_pic_hhmm;
      delete payload.initial_instructor_hhmm;
      if (isEdit) {
        await axios.put(`${API}/di/instructors/${instructor.id}`, payload, { withCredentials: true });
        toast.success("Instrutor atualizado");
      } else {
        await axios.post(`${API}/di/instructors`, payload, { withCredentials: true });
        toast.success("Instrutor criado");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileFormDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={isEdit ? `Editar ${instructor.name}` : "Novo instrutor"}
      description=""
      size="lg"
      onSubmit={submit}
      submitLabel={isEdit ? "Atualizar" : "Criar"}
      submitting={saving}
      testId="di-instructor-form-dialog"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="di-instructor-form">
        <div className="md:col-span-2">
          <Label>Nome *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="di-form-name" />
        </div>
        {isEdit && (
          <div className="md:col-span-2">
            <Label>Conta de acesso (e-mail com que a pessoa entra na app)</Label>
            <Input type="email" value={form.conta_email} onChange={(e) => setForm({ ...form, conta_email: e.target.value })} placeholder="vazio = sem conta ligada" data-testid="di-form-conta" />
            <p className="text-[10px] text-slate-500 mt-1">A conta ligada vê as reservas e os seus documentos. Deixe vazio para desligar.</p>
          </div>
        )}
        <div>
          <Label>Nº PU *</Label>
          <Input value={form.pu_number} onChange={(e) => setForm({ ...form, pu_number: e.target.value })} data-testid="di-form-pu" />
        </div>
        <div>
          <Label>Validade Licença PU (Piloto)</Label>
          <Input type="date" value={form.pu_license_valid_until || ""} onChange={(e) => setForm({ ...form, pu_license_valid_until: e.target.value })} data-testid="di-form-pu-license-until" disabled={bloqueados.has("pu_license_valid_until")} />{bloqueados.has("pu_license_valid_until") && <p className="text-[10px] text-slate-500 mt-1">{AVISO_DOCUMENTO}</p>}
        </div>
        <div>
          <Label>Nº Cert. médico</Label>
          <Input value={form.medical_cert_number || ""} onChange={(e) => setForm({ ...form, medical_cert_number: e.target.value })} />
        </div>
        <div>
          <Label>Validade Cert. médico</Label>
          <Input type="date" value={form.medical_valid_until || ""} onChange={(e) => setForm({ ...form, medical_valid_until: e.target.value })} data-testid="di-form-medical-until" disabled={bloqueados.has("medical_valid_until")} />{bloqueados.has("medical_valid_until") && <p className="text-[10px] text-slate-500 mt-1">{AVISO_DOCUMENTO}</p>}
        </div>
        <div>
          <Label>Emissão Licença Instrutor{form.instructor_license_valid_until && <span className="text-red-400 ml-1">*</span>}</Label>
          <Input type="date" value={form.instructor_license_issued_at || ""} onChange={(e) => setForm({ ...form, instructor_license_issued_at: e.target.value })} data-testid="di-form-license-issued" />
          <p className="text-[10px] text-slate-500 mt-1">Necessário para validar 'Instrutor de Instrutores' (≥3 anos).</p>
        </div>
        <div>
          <Label>Validade Licença Instrutor</Label>
          <Input type="date" value={form.instructor_license_valid_until || ""} onChange={(e) => setForm({ ...form, instructor_license_valid_until: e.target.value })} data-testid="di-form-license-until" disabled />
          <p className="text-[10px] text-slate-500 mt-1">Vem da licença PU carregada em Documentos anexos (a data da licença de instrutor).</p>
        </div>
        <div>
          <Label>Estado da licença</Label>
          <Select value={form.license_status ?? "none"} onValueChange={(v) => setForm({ ...form, license_status: v === "none" ? null : v })}>
            <SelectTrigger data-testid="di-form-license-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum (instrutor sem restrições)</SelectItem>
              <SelectItem value="R">R — Restrito</SelectItem>
              <SelectItem value="S">S — Instrutor de Instrutores</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Prova com examinador (ProfCheck) — data</Label>
          <Input type="date" value={form.proof_check_date || ""} onChange={(e) => setForm({ ...form, proof_check_date: e.target.value })} data-testid="di-form-proof-check" />
          <p className="text-[10px] text-slate-500 mt-1">Só a data da prova. A validade introduz-se à mão quando chegar o documento da ANAC.</p>
        </div>
        <div className="md:col-span-2 mt-1 p-3 rounded-md border border-amber-500/20 bg-amber-500/5">
          <div className="text-xs font-semibold text-amber-300 mb-2 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> Transporte de horas acumuladas iniciais
          </div>
          <p className="text-[11px] text-slate-400 mb-3">
            Para instrutores que já voavam antes de começar a usar a app. Estas horas
            <strong> entram nos totais all-time</strong> mas <strong>NÃO</strong> são
            contadas na janela rolling de 12 meses (que valida ANAC ≥15h e ≥25h instrução).
            Formato <code>HH:mm</code> (ex: <code>5234:30</code>).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Horas iniciais — PIC</Label>
              <Input inputMode="numeric" placeholder="0:00" value={form.initial_pic_hhmm} onChange={(e) => setForm({ ...form, initial_pic_hhmm: e.target.value })} data-testid="di-form-initial-pic" className="font-mono" />
            </div>
            <div>
              <Label>Horas iniciais — Instrutor</Label>
              <Input inputMode="numeric" placeholder="0:00" value={form.initial_instructor_hhmm} onChange={(e) => setForm({ ...form, initial_instructor_hhmm: e.target.value })} data-testid="di-form-initial-instr" className="font-mono" />
            </div>
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <Label>Qualificações</Label>
            <Button size="sm" variant="outline" onClick={addQual} data-testid="di-form-add-qual">
              <Plus className="w-3 h-3 mr-1" /> Adicionar
            </Button>
          </div>
          {form.qualifications.length === 0 && <div className="text-xs text-slate-500 italic">Sem qualificações registadas.</div>}
          <div className="space-y-2">
            {form.qualifications.map((q, i) => (
              <div key={`qual-${i}-${q.type}`} className="grid grid-cols-12 gap-2 items-center">
                <Select value={q.type} onValueChange={(v) => updateQual(i, "type", v)}>
                  <SelectTrigger className="col-span-5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(QUAL_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {QUAL_NO_DATES.has(q.type) ? (
                  <span className="col-span-6 text-[10px] text-slate-500 italic px-2">Sem datas — qualificação sem prazo de validade</span>
                ) : (
                  <>
                    <Input type="date" value={q.valid_from || ""} onChange={(e) => updateQual(i, "valid_from", e.target.value)} className="col-span-3" placeholder="Início" />
                    <Input type="date" value={q.valid_until || ""} onChange={(e) => updateQual(i, "valid_until", e.target.value)} className="col-span-3" placeholder="Validade" />
                  </>
                )}
                <Button size="icon" variant="ghost" onClick={() => removeQual(i)} className="col-span-1 text-red-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Notas</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
        </div>
        {isEdit && (
          <div className="md:col-span-2">
            <DocumentsManager ownerKind="instructor" ownerId={instructor.id} />
          </div>
        )}
        {!isEdit && (
          <div className="md:col-span-2 border border-dashed border-slate-700 rounded p-3 text-[11px] text-slate-500 italic">
            Cópias de documentos podem ser carregadas depois de criar o instrutor.
          </div>
        )}
      </div>
    </MobileFormDialog>
  );
}
